import { compactData, dateValue, stringValue } from "./firestore";
import type { GPSShareOptions } from "./gpsShare";

// Management-side model for gpsShareLinks/{shareId}: the unguessable UUID doc
// ID is the bearer token for the public /share/gps/:shareId page. Only league
// managers/admins can read or write these — the public page never touches
// Firestore, so the excluded user IDs and the creator's uid stay server-side.

export const GPS_SHARE_EXCLUDED_USER_CAP = 200;

/** Privacy-conservative: the map, names and status are the public baseline. */
export const DEFAULT_GPS_SHARE_OPTIONS: GPSShareOptions = {
  showCourse: true,
  showDeviceList: true,
  showSpeed: false,
  showBattery: false,
  showDeviceIDs: false,
  showCategory: false,
};

export interface GPSShareLink {
  id: string;
  leagueID: string;
  competitionInstanceID: string;
  enabled: boolean;
  expiresAt: Date | null;
  title: string | null;
  options: GPSShareOptions;
  excludedUserIDs: string[];
  createdByUserID?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

/** Fail closed: an absent or malformed flag reads as OFF, mirroring the server. */
export function parseGPSShareOptions(value: unknown): GPSShareOptions {
  const map = value && typeof value === "object" ? (value as Record<string, unknown>) : {};
  return {
    showCourse: map.showCourse === true,
    showSpeed: map.showSpeed === true,
    showBattery: map.showBattery === true,
    showDeviceIDs: map.showDeviceIDs === true,
    showDeviceList: map.showDeviceList === true,
    showCategory: map.showCategory === true,
  };
}

export function parseGPSShareLink(id: string, data: Record<string, unknown>): GPSShareLink | null {
  const leagueID = stringValue(data.leagueID);
  const competitionInstanceID = stringValue(data.competitionInstanceID);
  if (!leagueID || !competitionInstanceID) return null;

  return {
    id,
    leagueID,
    competitionInstanceID,
    enabled: data.enabled === true,
    expiresAt: dateValue(data.expiresAt) ?? null,
    title: stringValue(data.title) ?? null,
    options: parseGPSShareOptions(data.options),
    excludedUserIDs: (Array.isArray(data.excludedUserIDs) ? data.excludedUserIDs : [])
      .filter((entry): entry is string => typeof entry === "string" && entry.length > 0)
      .slice(0, GPS_SHARE_EXCLUDED_USER_CAP),
    createdByUserID: stringValue(data.createdByUserID),
    createdAt: dateValue(data.createdAt),
    updatedAt: dateValue(data.updatedAt),
  };
}

export function gpsShareLinkFirestoreData(link: GPSShareLink): Record<string, unknown> {
  return compactData({
    id: link.id,
    leagueID: link.leagueID,
    competitionInstanceID: link.competitionInstanceID,
    enabled: link.enabled,
    expiresAt: link.expiresAt ?? undefined,
    title: link.title ?? undefined,
    options: { ...link.options },
    excludedUserIDs: link.excludedUserIDs.slice(0, GPS_SHARE_EXCLUDED_USER_CAP),
    createdByUserID: link.createdByUserID,
    createdAt: link.createdAt,
    updatedAt: link.updatedAt,
  });
}

export function makeGPSShareLink(input: {
  id: string;
  leagueID: string;
  competitionInstanceID: string;
  createdByUserID?: string;
}): GPSShareLink {
  return {
    id: input.id,
    leagueID: input.leagueID,
    competitionInstanceID: input.competitionInstanceID,
    enabled: true,
    expiresAt: null,
    title: null,
    options: { ...DEFAULT_GPS_SHARE_OPTIONS },
    excludedUserIDs: [],
    createdByUserID: input.createdByUserID,
  };
}

/** True when the link would currently serve data (mirrors the server predicate). */
export function isGPSShareLinkLive(link: GPSShareLink, now: Date = new Date()): boolean {
  if (!link.enabled) return false;
  return link.expiresAt === null || link.expiresAt.getTime() > now.getTime();
}

export function isGPSShareLinkExpired(link: GPSShareLink, now: Date = new Date()): boolean {
  return link.expiresAt !== null && link.expiresAt.getTime() <= now.getTime();
}
